import React, { useState } from 'react';
import { RecentProject, UserRole } from '../../../user/user.type';
import FileUpload, { FileUploadType } from '../../../ui/file-upload.ui';
import InputUi, { InputType } from '../../../ui/input.ui';
import TextFieldUi from '../../../ui/text-field.ui';
import TagInputUi from '../../../ui/tag-input.ui';
import TagSelectorUi from '../../../ui/tag-selector.ui';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Loader from '../../../ui/loader.ui';

import styles from './onboarding-step-recent-project.module.css';

enum RecentProjectDescriptionPlaceholder {
  Founder = 'Startup Description - This is to show the devs what\'s interesting about your startup! What makes your startup unique? The more you share the better!',
  SoftwareEngineer = 'Project Description - Please share some details about a tech project that you want to share with the hiring founder. What makes your project relevant? The more you share the better!',
}

type OnboardingStepRecentProjectUiProps = {
  projectImagePreview: string | null;
  projectName: string;
  projectDescription: string;
  projectDescriptionCount: number;
  role: UserRole | '';
  projectCategory?: string[];
  techStack?: string[];
  developerTechStack?: string[];
  projectCategoryTags: string[];
  projectCategoryTagsLoading: boolean;
  projectCategoriesMaxSelection: number;
  projectPrefabImages: string[];
  projectPrefabImagesLoading: boolean;
  projectImageError: string | undefined;
  projectNameError: string | undefined;
  projectDescriptionError: string | undefined;
  techStackError: string | undefined;
  developerTechStackError: string | undefined;
  fetchErrorProjectCategory: string | null;
  projectCategoryError: string | undefined;
  isSubmitting: boolean;
  isUploadingProjectImage: boolean;
  onUpdateProjectField: (field: keyof RecentProject, value: string) => void;
  onProjectImageSelect: (file: File | null) => void;
  onProjectImageError: (error: string | null) => void;
  onPrefabImageSelect: (imageUrl: string) => void;
  onAddTechStackSkill: (skill: string) => void;
  onRemoveTechStackSkill: (skill: string) => void;
  onAddDeveloperTechStackSkill: (skill: string) => void;
  onRemoveDeveloperTechStackSkill: (skill: string) => void;
  onAddProjectCategory: (category: string) => void;
  onRemoveProjectCategory: (category: string) => void;
}

const OnboardingStepRecentProjectUi = (props: OnboardingStepRecentProjectUiProps) => {
  const {
    projectImagePreview,
    projectName,
    projectDescription,
    projectDescriptionCount,
    role,
    techStack,
    developerTechStack,
    projectCategory,
    projectCategoryTags,
    projectCategoryTagsLoading,
    projectCategoriesMaxSelection,
    projectPrefabImages,
    projectPrefabImagesLoading,
    projectImageError,
    projectNameError,
    projectDescriptionError,
    techStackError,
    developerTechStackError,
    fetchErrorProjectCategory,
    projectCategoryError,
    isSubmitting,
    isUploadingProjectImage,
    onUpdateProjectField,
    onProjectImageSelect,
    onProjectImageError,
    onPrefabImageSelect,
    onAddTechStackSkill,
    onRemoveTechStackSkill,
    onAddDeveloperTechStackSkill,
    onRemoveDeveloperTechStackSkill,
    onAddProjectCategory,
    onRemoveProjectCategory,
  } = props;

  const [dialogOpen, setDialogOpen] = useState(false);

  const toggleDialog = () => {
    setDialogOpen(prev => !prev);
  };

  const handlePrefabImageSelect = (imageUrl: string) => {
    onPrefabImageSelect(imageUrl);
    toggleDialog();
  };

  return (
    <>
      <div className={styles.imagesWrapper}>
        <div className={styles.imageWrapper}>
          <div className={styles.title}>Upload logo, screenshot or other picture</div>
          <FileUpload
            uploadType={FileUploadType.image}
            currentFile={projectImagePreview}
            onFileChange={onProjectImageSelect}
            onError={onProjectImageError}
            isUploading={isUploadingProjectImage}
            error={projectImageError || undefined}
          />
        </div>

        {/* Prefab Images Button */}
        <div className={styles.prefabSection}>
          <div className={styles.title}>Or choose a prefab image:</div>
          <button
            type="button"
            className={styles.imageButton}
            onClick={toggleDialog}
            disabled={isSubmitting || projectPrefabImagesLoading}
          >
            Choose an image
          </button>
        </div>
      </div>

      {/* Prefab Images Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={toggleDialog}
        maxWidth="sm"
        fullWidth
        disableRestoreFocus
      >

        <DialogContent>
          {projectPrefabImagesLoading ? (
            <Loader />
          ) : (
            <div className={styles.prefabImages}>
              {projectPrefabImages.map((image, index) => (
                <img
                  key={index}
                  src={image}
                  alt={`Template ${index + 1}`}
                  className={styles.prefabImage}
                  onClick={() => handlePrefabImageSelect(image)}
                />
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <InputUi
        id={'projectName'}
        type={InputType.text}
        value={projectName}
        placeholder={'Project Name'}
        error={projectNameError}
        onChange={(val) => onUpdateProjectField('name', val)}
      />

      <TextFieldUi
        id={'projectDescription'}
        value={projectDescription}
        rows={3}
        placeholder={role === UserRole.Founder
          ? RecentProjectDescriptionPlaceholder.Founder
          : RecentProjectDescriptionPlaceholder.SoftwareEngineer
        }
        error={projectDescriptionError}
        counter={projectDescriptionCount}
        minLength={100}
        maxLength={role === UserRole.Founder ? 350 : 500}
        onChange={(val) => onUpdateProjectField('description', val)}
        disabled={isSubmitting}
      />

      {role === UserRole.SoftwareEngineer ? (
        <>
          <TagInputUi
            id={'techStack'}
            values={techStack || []}
            onAdd={onAddTechStackSkill}
            placeholder="Enter project tech stack (press Enter to add)"
            onRemove={onRemoveTechStackSkill}
            disabled={isSubmitting}
          />
          {techStackError && <p className={styles.error}>{techStackError}</p>}

          <TagInputUi
            id={'developerTechStack'}
            values={developerTechStack || []}
            onAdd={onAddDeveloperTechStackSkill}
            placeholder="Enter your personal tech stack (press Enter to add)"
            onRemove={onRemoveDeveloperTechStackSkill}
            disabled={isSubmitting}
          />
          {developerTechStackError && <p className={styles.error}>{developerTechStackError}</p>}
        </>
      ) : (
        <>
          <div className={styles.title}>Tick any that apply to your startup</div>
          <TagSelectorUi
            tags={projectCategoryTags || []}
            selectedTags={projectCategory || []}
            loading={projectCategoryTagsLoading}
            error={fetchErrorProjectCategory}
            maxSelection={projectCategoriesMaxSelection}
            onAddTag={onAddProjectCategory}
            onRemoveTag={onRemoveProjectCategory}
            disabled={isSubmitting}
          />
          {projectCategoryError && (
            <p className={styles.error}>{projectCategoryError}</p>
          )}
        </>
      )}
    </>
  );
};

export default OnboardingStepRecentProjectUi;
